import { Component, OnInit, inject, signal } from '@angular/core';
import { ApiClientService } from '../../core/api/api-client.service';
import { AuditLogSummary, PageResponse } from '../../core/models/portal.models';

@Component({
  selector: 'app-audit-log-page',
  standalone: true,
  templateUrl: './audit-log-page.component.html'
})
export class AuditLogPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);

  protected readonly logs = signal<PageResponse<AuditLogSummary> | null>(null);
  protected readonly search = signal('');
  protected readonly action = signal('');
  protected readonly page = signal(0);
  protected readonly pageSize = signal(10);
  protected readonly loading = signal(false);

  ngOnInit(): void {
    this.load(0);
  }

  protected load(page = this.page()): void {
    this.loading.set(true);
    this.api.auditLogs(page, this.pageSize(), this.search(), this.action()).subscribe({
      next: (logs) => {
        this.logs.set(logs);
        this.page.set(logs.page);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  protected applyFilters(): void {
    this.load(0);
  }

  protected reset(): void {
    this.search.set('');
    this.action.set('');
    this.load(0);
  }

  protected setSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected setAction(event: Event): void {
    this.action.set((event.target as HTMLSelectElement).value);
  }

  protected setPageSize(event: Event): void {
    this.pageSize.set(Number((event.target as HTMLSelectElement).value));
    this.load(0);
  }

  protected previousPage(): void {
    const current = this.logs();
    if (current && this.hasPrevious(current)) {
      this.load(current.page - 1);
    }
  }

  protected nextPage(): void {
    const current = this.logs();
    if (current && this.hasNext(current)) {
      this.load(current.page + 1);
    }
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected target(log: AuditLogSummary): string {
    return [log.organizationName, log.companyName, log.driverName, log.courseTitle].filter(Boolean).join(' · ') || '-';
  }

  protected formatDate(value: string): string {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  }

  protected actionLabel(action: string): string {
    const labels: Record<string, string> = {
      CREATED: 'Angelegt',
      UPDATED: 'Aktualisiert',
      DELETED: 'Gelöscht',
      LOGIN: 'Login',
      LOGOUT: 'Logout',
      LOGIN_FAILED: 'Login fehlgeschlagen',
      ORGANIZATION_CREATED: 'Organisation angelegt',
      ORGANIZATION_UPDATED: 'Organisation aktualisiert',
      ORGANIZATION_DELETED: 'Organisation gelöscht',
      COMPANY_CREATED: 'Unternehmer angelegt',
      COMPANY_UPDATED: 'Unternehmer aktualisiert',
      COMPANY_DELETED: 'Unternehmer gelöscht',
      DRIVER_CREATED: 'Fahrer angelegt',
      DRIVER_UPDATED: 'Fahrer aktualisiert',
      DRIVER_DELETED: 'Fahrer gelöscht',
      COURSE_ASSIGNED: 'Schulung zugewiesen',
      LESSON_COMPLETED: 'Lektion abgeschlossen',
      QUIZ_STARTED: 'Quiz gestartet',
      QUIZ_PASSED: 'Quiz bestanden',
      QUIZ_FAILED: 'Quiz nicht bestanden',
      CERTIFICATE_ISSUED: 'Zertifikat ausgestellt',
      CERTIFICATE_REVOKED: 'Zertifikat widerrufen',
      RETRAINING_ASSIGNED: 'Nachschulung zugewiesen',
      RETRAINING_COMPLETED: 'Nachschulung abgeschlossen',
      PAYMENT_INITIATED: 'Zahlung gestartet',
      PAYMENT_COMPLETED: 'Zahlung abgeschlossen',
      PAYMENT_FAILED: 'Zahlung fehlgeschlagen',
      PAYMENT_RETURNED: 'Zur Anwendung zurückgekehrt',
      VIDEO_WATCHED: 'Video angesehen'
    };
    return labels[action] ?? action;
  }

  protected totalPages(page: PageResponse<AuditLogSummary>): number {
    return Math.max(1, Math.ceil(page.total / page.size));
  }

  protected hasPrevious(page: PageResponse<AuditLogSummary> | null): boolean {
    return !!page && page.page > 0;
  }

  protected hasNext(page: PageResponse<AuditLogSummary>): boolean {
    return (page.page + 1) * page.size < page.total;
  }
}
